/**
 * Shipping-protection A/B in the cart summary (drawer + /cart) — ab.hc.tools `custom` experiment, Oct 2026.
 * Ported from Afroyla's afroyla-cart-protection.js (live 12.10.2026); only ids, names and the stale-fee refresh differ.
 *
 * 1. Arm: reads the visitor's assignment (window.__HCAB, synchronous in <head>) and sets
 *    <html data-sk-protect="control|basic">; skullette-cart-protection.css shows that arm's card.
 *    QA: ?sk_protect=basic|control forces an arm for this tab (sessionStorage; exposure is NOT counted),
 *    ?sk_protect=off clears it.
 * 2. Exposure: counted for EVERY arm (custom counts nothing otherwise) the first time the visitor sees the cart
 *    summary WITH the protection block rendered (it renders for every arm, hidden for control) — the open cart
 *    drawer or /cart. An empty cart or a missing fee product renders no block, so it counts in no arm.
 * 3. Checkout: "Protected checkout" swaps any protection line for the fee variant Liquid rendered, then goes to
 *    /checkout; "Continue without protection" removes any protection line, then goes to /checkout. The same
 *    call writes the A/B attribution attributes (the page leaves before ab.js can). A failed or slow (> 4 s)
 *    cart call never blocks the purchase — the shopper still lands on /checkout.
 * 4. Stale fee: a fee line only has to live between that click and /checkout. Whenever a theme page loads (or is
 *    restored from the back-forward cache) with a fee line still in the cart, it is removed in EVERY arm and the
 *    page reloads once, so a shopper back from checkout is never charged a fee the cart doesn't show. Keep this
 *    file live after the test ends for the same reason (carts live ~14 days).
 * The fee line carries `_is_protection` (hidden at checkout; snippets/cart-products.liquid skips it).
 */
(function () {
  if (window.__skProtect) return;
  window.__skProtect = true;

  var EXP = 'a67fd089-cfe4-4988-a618-242cfe99b5a3';
  var ARMS = {
    '23ee0e3f-0498-4c4e-b337-c413ae216551': 'control',
    '2d38fca1-d6cd-436c-aeb6-4cc3e0b6a6ff': 'basic',
  };
  var QA_KEY = 'sk_protect_qa';
  var RELOAD_KEY = 'sk_protect_reloaded';
  var TIMEOUT_MS = 4000;
  var root = document.documentElement;
  var arm = 'control';
  var qa = null;

  function readQa() {
    try {
      var q = new URLSearchParams(window.location.search).get('sk_protect');
      if (q === 'off') window.sessionStorage.removeItem(QA_KEY);
      else if (q === 'basic' || q === 'control') window.sessionStorage.setItem(QA_KEY, q);
      return window.sessionStorage.getItem(QA_KEY);
    } catch (e) {
      return null;
    }
  }

  function assignment() {
    var hc = window.__HCAB;
    return hc && hc.assignments ? hc.assignments[EXP] : null;
  }

  function resolveArm() {
    qa = readQa();
    arm = qa || ARMS[assignment()] || 'control';
    root.setAttribute('data-sk-protect', arm);
  }

  /* ---- exposure ---------------------------------------------------------------------------------------- */
  var exposed = false;
  function expose() {
    if (exposed || qa) return;
    var hc = window.__HCAB;
    if (!assignment() || !hc || typeof hc.exposure !== 'function') return;
    exposed = true;
    try { hc.exposure(EXP); } catch (e) { /* never break the cart over analytics */ }
  }
  function cartDrawerOpen() {
    var d = document.querySelector('cart-drawer-component');
    d = d && d.closest('dialog');
    return !!(d && d.hasAttribute('open') && d.querySelector('.sk-protect'));
  }
  function onCartPage() {
    return /^\/cart\/?$/.test(window.location.pathname) && !!document.querySelector('.sk-protect');
  }
  function maybeExpose() {
    if (onCartPage() || cartDrawerOpen()) expose();
  }

  resolveArm();
  /* preview links resolve the assignment after load */
  document.addEventListener('hcab:ready', function () { resolveArm(); maybeExpose(); });
  maybeExpose();
  /* the drawer's <dialog> gaining [open], or its summary being re-rendered while open (cart was empty before) */
  var watcher = new MutationObserver(function () {
    maybeExpose();
    if (exposed) watcher.disconnect();
  });
  if (!exposed) watcher.observe(document.documentElement, { subtree: true, childList: true, attributes: true, attributeFilter: ['open'] });

  /* ---- cart calls ---------------------------------------------------------------------------------------- */
  function request(url, body, signal) {
    return fetch(url, {
      method: body ? 'POST' : 'GET',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: body ? JSON.stringify(body) : undefined,
      signal: signal,
    }).then(function (r) {
      if (!r.ok) throw new Error(url + ' ' + r.status);
      return r.json();
    });
  }

  /* A/B attribution on the cart (same shape ab.js writes after a cart call). */
  function abAttributes() {
    try {
      var all = (window.__HCAB && window.__HCAB.assignments) || {};
      var ids = Object.keys(all);
      var m = document.cookie.match(/(?:^|;\s*)_hcab_vid=([^;]+)/);
      if (!ids.length || !m) return null;
      return { _ab_visitor: decodeURIComponent(m[1]), _ab_assignments: ids.map(function (id) { return id + ':' + all[id]; }).join(',') };
    } catch (e) {
      return null;
    }
  }

  /* updates{} that zero every protection line in `cart` (null when there is none) */
  function protectionZeroes(cart) {
    var updates = {};
    var n = 0;
    (cart.items || []).forEach(function (item) {
      if (item.properties && item.properties._is_protection) { updates[item.key] = 0; n++; }
    });
    return n ? updates : null;
  }

  /* ---- stale fee cleanup (every arm, every theme page) ----------------------------------------------------- */
  /* Order-level marker CS reads in the admin order page ("Additional details"): set on every arm-button click,
     cleared together with a stale fee line. */
  var ORDER_ATTR = 'Shipping protection';

  var cleaning = false;
  var busy = false;
  function removeStaleFee() {
    if (cleaning || busy) return;
    cleaning = true;
    request('/cart.js')
      .then(function (cart) {
        var updates = protectionZeroes(cart);
        if (!updates) return false;
        var cleared = {};
        cleared[ORDER_ATTR] = '';
        return request('/cart/update.js', { updates: updates, attributes: cleared }).then(function () { return true; });
      })
      .then(function (changed) {
        /* re-render the drawer/cart so totals, counts and the card reflect the cart without the fee: one reload,
           guarded so a failing removal can never loop */
        if (!changed) return;
        try {
          if (window.sessionStorage.getItem(RELOAD_KEY)) return;
          window.sessionStorage.setItem(RELOAD_KEY, '1');
        } catch (e) { /* storage blocked: reload anyway, the next load finds no fee line */ }
        window.location.reload();
      })
      .catch(function () {})
      .then(function () { cleaning = false; });
  }
  /* a clean load clears the loop guard */
  if (!document.querySelector('[data-sk-protect-stale]')) {
    try { window.sessionStorage.removeItem(RELOAD_KEY); } catch (e) { /* ignore */ }
  } else {
    removeStaleFee();
  }

  /* ---- checkout ------------------------------------------------------------------------------------------- */
  function buttons() {
    return document.querySelectorAll('[data-sk-protect-go], [data-sk-protect-skip]');
  }
  function setBusy(on) {
    busy = on;
    buttons().forEach(function (b) { b.disabled = on; });
  }

  function goToCheckout(feeVariantId) {
    if (busy) return;
    setBusy(true);
    var ctrl = typeof AbortController === 'function' ? new AbortController() : null;
    var timer = setTimeout(function () { if (ctrl) ctrl.abort(); }, TIMEOUT_MS);
    var signal = ctrl ? ctrl.signal : undefined;
    request('/cart.js', null, signal)
      .then(function (cart) {
        var body = {};
        var updates = protectionZeroes(cart);
        var attrs = abAttributes() || {};
        attrs[ORDER_ATTR] = feeVariantId ? 'Protection' : 'Declined';
        if (updates) body.updates = updates;
        body.attributes = attrs;
        return request('/cart/update.js', body, signal);
      })
      .then(function () {
        if (!feeVariantId) return null;
        return request('/cart/add.js', {
          items: [{ id: Number(feeVariantId), quantity: 1, properties: { _is_protection: 'true', _protection_arm: arm } }],
        }, signal);
      })
      .catch(function (e) { if (window.console) console.warn('[sk-protect]', e); })
      .then(function () {
        clearTimeout(timer);
        window.location.href = '/checkout';
      });
  }

  /* the fee variant of the arm the visitor sees (drawer and /cart page render the same fee) */
  function visibleGo() {
    var el = document.querySelector('.sk-protect[data-sk-protect-arm="' + arm + '"] [data-sk-protect-go]');
    return el ? el.getAttribute('data-sk-protect-go') : null;
  }

  document.addEventListener('click', function (e) {
    var go = e.target.closest('[data-sk-protect-go]');
    if (go) { e.preventDefault(); goToCheckout(go.getAttribute('data-sk-protect-go')); return; }
    var skip = e.target.closest('[data-sk-protect-skip]');
    if (skip) { e.preventDefault(); goToCheckout(null); }
  });

  /* Enter in a quantity field submits #cart-form through the (hidden) #checkout button: in a protected arm,
     route it through the pre-selected protected checkout instead of skipping the choice. */
  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (!form || form.id !== 'cart-form' || arm === 'control') return;
    if (!document.querySelector('.cart__checkout-button--protect-ready')) return;
    var fee = visibleGo();
    if (!fee) return;
    e.preventDefault();
    goToCheckout(fee);
  }, true);

  /* Back from checkout restores this page from the bfcache: un-stick the buttons and drop the fee line. */
  window.addEventListener('pageshow', function (e) {
    if (!e.persisted) return;
    setBusy(false);
    removeStaleFee();
  });
})();
